import { formatEuro } from './currency';
import {
  parseInfluencerInvoices,
  parseMohitInvoices,
  getNetPayableBreakdown,
  getPayableOverrides,
  type PayableCampaignInput,
} from './paymentCalculations';
import { computePayableReadiness, getPayableReadinessForCampaign, formatReadyPositions } from './payableReadiness';
import { toMonthKey, formatMonthPt } from './avenca';
import { getExpenseKind, getOutstandingDeductions, isExpenseSettled, parseCampaignExpenses } from './campaignExpenses';

const ROUNDING_TOLERANCE = 0.5;

/**
 * What a creator is being paid, in their own terms: which of *their*
 * tranches ("3–6 de 9"), which months they refer to ("jun – set 2026") and
 * the amount of each — not the client's instalment count ("9/12").
 */
export interface PayoutTranche {
  position: number;
  of: number;
  amount: number;
  month: string | null; // "2026-06"
}

export interface PayoutExpenseLine {
  description: string;
  amount: number;
  /** When it was settled (settled deductions only) */
  date?: string | null;
}

export interface PayoutDetail {
  tranches: PayoutTranche[];
  /** Pass-through expenses reimbursed in this payout (split out of the last tranche) */
  reimbursements: PayoutExpenseLine[];
  /** Deductions withheld on this payout */
  deductions: PayoutExpenseLine[];
  /** Deductions already withheld on an earlier payout — shown, not subtracted */
  settledDeductions: PayoutExpenseLine[];
  /** Positions still waiting for the client */
  waitingPositions: number[];
  totalPositions: number;
  /** To pay now: the tranches minus any deduction withheld on this payout */
  amount: number;
  /** What the creator invoices: tranche lines plus reimbursements, before deductions */
  tranchesTotal: number;
  parcelsLabel: string;
  periodLabel: string;
}

type PayoutCampaign = PayableCampaignInput & {
  year?: number | null;
  delivery_date?: string | null;
  event_date?: string | null;
};

const MONTH_NAMES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const MONTH_ALIASES: Record<string, number> = {};
MONTH_NAMES.forEach((name, i) => {
  MONTH_ALIASES[name] = i;
  MONTH_ALIASES[name.slice(0, 3)] = i;
  MONTH_ALIASES[name.normalize('NFD').replace(/[̀-ͯ]/g, '')] = i;
});

const round2 = (n: number) => Math.round(n * 100) / 100;

/** "junho 2026" */
export function formatMonthLongPt(key: string | null | undefined): string {
  const k = toMonthKey(key);
  if (!k) return '';
  const [y, m] = k.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

function addMonths(key: string, n: number): string {
  const [y, m] = key.split('-').map(Number);
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/**
 * The month a client invoice bills, in order of reliability: the avença
 * month; a month written as the invoice number ("Agosto", as on XTB); for an
 * instalment plan (3+ invoices), the campaign's start month + its position
 * (Surfshark invoiced Aug and Sep both on 22 Sep — they are still Aug and
 * Sep); otherwise the month it was issued.
 */
function clientInvoiceMonth(
  inv: { period?: string; invoice_number?: string; issued_date?: string; payment_received_date?: string },
  index: number,
  count: number,
  startMonth: string | null,
  fallbackYear: number,
): string | null {
  const period = toMonthKey(inv.period);
  if (period) return period;
  const word = (inv.invoice_number || '').trim().toLowerCase();
  const plain = word.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const idx = MONTH_ALIASES[word] ?? MONTH_ALIASES[plain];
  if (idx !== undefined) {
    const year = Number((startMonth || '').slice(0, 4)) || Number((inv.issued_date || '').slice(0, 4)) || fallbackYear;
    return `${year}-${String(idx + 1).padStart(2, '0')}`;
  }
  if (startMonth && count >= 3) return addMonths(startMonth, index);
  return toMonthKey(inv.issued_date) || toMonthKey(inv.payment_received_date);
}

/** "ago 2026", "jun – set 2026", "dez 2025 – fev 2026" */
export function formatMonthRange(keys: string[]): string {
  const sorted = [...new Set(keys)].sort();
  if (sorted.length === 0) return '';
  if (sorted.length === 1) return formatMonthPt(sorted[0]);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const sameYear = first.slice(0, 4) === last.slice(0, 4);
  const start = sameYear ? formatMonthPt(first).split(' ')[0] : formatMonthPt(first);
  return `${start} – ${formatMonthPt(last)}`;
}

/**
 * @param netPayable the payable from the money rule — used when the campaign
 *   has no tranches to describe
 * @param creatorName multi-influencer: only this creator's payables, funded
 *   by `funded` (paid out + available)
 */
export function getPayoutDetail(
  campaign: PayoutCampaign,
  { netPayable, creatorName, funded }: { netPayable: number; creatorName?: string; funded?: number },
): PayoutDetail {
  const allPayables = parseInfluencerInvoices(campaign);
  const payables = creatorName ? allPayables.filter((p) => p.source_name === creatorName) : allPayables;
  const clients = parseMohitInvoices(campaign) as { amount_received?: number; period?: string; invoice_number?: string; issued_date?: string; payment_received_date?: string; status?: string; id?: string }[];

  // Once the client has paid everything, nothing waits on the client — even
  // when a stored tranche is out of date with the commission (TCL 2026/94)
  const clientFullyPaid = clients.length > 0 && clients.every((c) => c.status === 'Paid');
  const readiness = (creatorName
    ? computePayableReadiness(payables, clients, () => funded ?? 0, { pooled: true })
    : getPayableReadinessForCampaign(campaign)
  ).map((r) => (r === 'waiting' && clientFullyPaid ? 'ready' : r));

  // Which client instalment completes the funding of each tranche: compare
  // cumulative shares, since the creator's plan rarely has the same number
  // of tranches as the client's (Surfshark: 9 vs 12)
  const plannedTotal = payables.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const clientTotal = clients.reduce((s, c) => s + (Number(c.amount_received) || 0), 0);
  const fallbackYear = Number(campaign.year) || new Date().getFullYear();
  const startMonth = toMonthKey(
    (campaign as { billing_start?: string | null }).billing_start || campaign.delivery_date || campaign.event_date || null
  );
  const clientCumShare: number[] = [];
  clients.reduce((acc, c) => {
    const next = acc + (Number(c.amount_received) || 0);
    clientCumShare.push(clientTotal > 0 ? next / clientTotal : 0);
    return next;
  }, 0);

  let running = 0;
  const tranches: PayoutTranche[] = [];
  const waitingPositions: number[] = [];
  payables.forEach((p, i) => {
    running += Number(p.amount) || 0;
    if (readiness[i] === 'waiting') waitingPositions.push(i + 1);
    if (readiness[i] !== 'ready') return;
    const share = plannedTotal > 0 ? running / plannedTotal : 0;
    const j = clientCumShare.findIndex((g) => g >= share - 0.0005);
    tranches.push({
      position: i + 1,
      of: payables.length,
      amount: round2(Number(p.amount) || 0),
      month: j >= 0 ? clientInvoiceMonth(clients[j], j, clients.length, startMonth, fallbackYear) : null,
    });
  });

  const expenses = parseCampaignExpenses(campaign);
  const settledDeductions: PayoutExpenseLine[] = creatorName ? [] : expenses
    .filter((e) => getExpenseKind(e) === 'deduction' && (e.amount || 0) > 0 && isExpenseSettled(e))
    .map((e) => ({ description: e.description || 'Despesa', amount: round2(e.amount || 0), date: e.settled_date || null }));

  // Outstanding deductions withheld now: itemised when they all fit this
  // payout, otherwise one line for the part actually withheld
  const { deductionsApplied } = creatorName
    ? { deductionsApplied: 0 }
    : getNetPayableBreakdown(campaign, getPayableOverrides(campaign as { campaign_mode?: string | null }));
  const outstanding = creatorName ? [] : getOutstandingDeductions(campaign);
  const outstandingTotal = outstanding.reduce((s, e) => s + (e.amount || 0), 0);
  const deductions: PayoutExpenseLine[] = deductionsApplied <= 0.005
    ? []
    : Math.abs(outstandingTotal - deductionsApplied) < 0.01
      ? outstanding.map((e) => ({ description: e.description || 'Despesa', amount: round2(e.amount || 0) }))
      : [{ description: 'Despesas', amount: round2(deductionsApplied) }];

  if (tranches.length === 0) {
    const ref = toMonthKey(campaign.delivery_date || campaign.event_date || null);
    return {
      tranches,
      reimbursements: [],
      deductions,
      settledDeductions,
      waitingPositions,
      totalPositions: payables.length,
      amount: round2(netPayable),
      tranchesTotal: round2(netPayable + deductionsApplied),
      parcelsLabel: payables.length > 0 ? `— de ${payables.length}` : '—',
      periodLabel: ref ? formatMonthPt(ref) : '—',
    };
  }

  // The amount due comes from the money rule; if the stored tranches drifted
  // from it, the last line absorbs the difference so the lines add up
  const drift = round2(netPayable - (tranches.reduce((s, t) => s + t.amount, 0) - deductionsApplied));
  const last = tranches[tranches.length - 1];
  if (Math.abs(drift) > ROUNDING_TOLERANCE) {
    last.amount = round2(Math.max(0, last.amount + drift));
  }
  const tranchesTotal = round2(tranches.reduce((s, t) => s + t.amount, 0));

  // Pass-through expenses travel with the payout that closes the campaign
  // (unless already marked as paid): shown as their own lines so the creator
  // sees what is a reimbursement (GALP 2026/124: 5 950 € + 539,70 €)
  const pendingPassthrough = creatorName ? [] : expenses
    .filter((e) => getExpenseKind(e) === 'passthrough' && (e.amount || 0) > 0 && !isExpenseSettled(e));
  const passthroughTotal = round2(pendingPassthrough.reduce((s, e) => s + (e.amount || 0), 0));
  const closesCampaign = last.position === payables.length;
  const reimbursements: PayoutExpenseLine[] = [];
  if (passthroughTotal > 0 && closesCampaign && last.amount >= passthroughTotal - 0.005) {
    last.amount = round2(last.amount - passthroughTotal);
    pendingPassthrough.forEach((e) => reimbursements.push({ description: e.description || 'Despesa', amount: round2(e.amount || 0) }));
  }

  const months = tranches.map((t) => t.month).filter((m): m is string => !!m);

  return {
    tranches,
    reimbursements,
    deductions,
    settledDeductions,
    waitingPositions,
    totalPositions: payables.length,
    amount: round2(Math.max(0, tranchesTotal - deductionsApplied)),
    tranchesTotal,
    parcelsLabel: `${formatReadyPositions(readiness)} de ${payables.length}`,
    periodLabel: months.length > 0 ? formatMonthRange(months) : '—',
  };
}

function dots(label: string, value: string, width = 44): string {
  const gap = Math.max(2, width - label.length - value.length);
  return `${label} ${'.'.repeat(gap)} ${value}`;
}

const SEP = '━━━━━━━━━━━━━━━━━━━━━━━━━━';

/** "2026-08-18" → "18/08/2026" */
function formatDayPt(date: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/**
 * The "Copiar para Email" message for one creator: per campaign, each
 * tranche with its month, the expenses reimbursed with it, the deductions
 * withheld now (and those already withheld before, for reference); then the
 * invoice the creator issues and what is transferred.
 */
export function buildCreatorPayoutEmail(
  creatorName: string,
  rows: { campaign: PayoutCampaign & { work_code?: string | null; brand?: string | null; client?: string | null; campaign_ref?: string | null }; detail: PayoutDetail }[],
): string {
  const blocks = rows.map(({ campaign, detail }) => {
    const title = `${campaign.work_code || '—'} · ${campaign.brand || campaign.client || '—'}${campaign.campaign_ref ? ` — ${campaign.campaign_ref}` : ''}`;
    const lines = detail.tranches.length > 0
      ? detail.tranches.map((t) => '  ' + dots(
          `Parcela ${t.position} de ${t.of}${t.month ? ` · ${formatMonthLongPt(t.month)}` : ''}`,
          formatEuro(t.amount),
        ))
      : ['  ' + dots(`Pagamento${detail.periodLabel !== '—' ? ` · ${detail.periodLabel}` : ''}`, formatEuro(detail.tranchesTotal))];
    detail.reimbursements.forEach((e) => lines.push('  ' + dots(`+ ${e.description}`, formatEuro(e.amount))));
    detail.deductions.forEach((e) => lines.push('  ' + dots(`(-) ${e.description}`, `-${formatEuro(e.amount)}`)));
    if (lines.length > 1) lines.push('  ' + dots('Subtotal', formatEuro(detail.amount)));
    detail.settledDeductions.forEach((e) => {
      const when = formatDayPt(e.date);
      lines.push(`  Já descontado${when ? ` em ${when}` : ' anteriormente'}: ${e.description} (-${formatEuro(e.amount)})`);
    });
    if (detail.waitingPositions.length > 0) {
      const w = detail.waitingPositions;
      const label = w.length === 1 ? `parcela ${w[0]}` : `parcelas ${w[0]} a ${w[w.length - 1]}`;
      lines.push(`  Por pagar: ${label} (aguardam pagamento do cliente)`);
    }
    return [title, ...lines].join('\n');
  });

  const netTotal = round2(rows.reduce((s, r) => s + r.detail.amount, 0));
  const invoiceTotal = round2(rows.reduce((s, r) => s + r.detail.tranchesTotal, 0));
  const deductionsTotal = round2(rows.reduce((s, r) => s + r.detail.deductions.reduce((d, e) => d + e.amount, 0), 0));
  const totals = deductionsTotal > 0
    ? [
        SEP,
        `Subtotal: ${formatEuro(invoiceTotal)}`,
        `(-) Despesas descontadas: -${formatEuro(deductionsTotal)}`,
        `TOTAL A PAGAR: ${formatEuro(netTotal)}`,
        SEP,
      ]
    : [SEP, `TOTAL: ${formatEuro(netTotal)}`, SEP];

  return [
    `Olá ${creatorName},`,
    '',
    deductionsTotal > 0
      ? 'Segue informação sobre os pagamentos prontos e a respetiva dedução de despesas:'
      : 'Segue informação sobre pagamentos prontos:',
    '',
    blocks.join('\n\n'),
    '',
    ...totals,
    `Valor da fatura a emitir: ${formatEuro(invoiceTotal)} + IVA`,
    '',
    'Obrigado,',
    'Equipa Mohit',
  ].join('\n');
}
